'use client';

import { type RoadmapResource } from '@/domain/resources/types';
import { validatePlannedHours } from '@/features/roadmap/utils/planningHours';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Autocomplete } from '@andritzot/metris-web-ui/inputs/autocomplete';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { IconButton } from '@andritzot/metris-web-ui/inputs/icon-button';
import { TextField } from '@andritzot/metris-web-ui/inputs/textfield';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { Stack } from '@andritzot/metris-web-ui/layout/stack';
import { DataGrid, type GridColDef } from '@andritzot/metris-web-ui/mui-x/data-grid';
import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined';
import { memo, useMemo } from 'react';

import { HoursCellInput } from './HoursCellInput';
import { type ActivityRow, usePlannedHoursSection } from './usePlannedHoursSection';

interface PlannedHoursSectionProps {
  viewOnly?: boolean;
  createRoadmapMode?: boolean;
  isClone?: boolean;
}

interface GridControlsProps {
  resources: RoadmapResource[];
  alreadyAdded: Set<number>;
  pendingResources: RoadmapResource[];
  onPendingChange: (resources: RoadmapResource[]) => void;
  onAdd: (resources: RoadmapResource[]) => void;
  showAssignDefaults: boolean;
  canAssignDefaults: boolean;
  onAssignDefaults: () => void;
}

const GridControls = memo(function GridControls({
  resources,
  alreadyAdded,
  pendingResources,
  onPendingChange,
  onAdd,
  showAssignDefaults,
  canAssignDefaults,
  onAssignDefaults,
}: Readonly<GridControlsProps>) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ width: 300 }}>
        <Autocomplete<RoadmapResource, true, false, false>
          multiple
          disableCloseOnSelect
          options={resources.filter((r) => !alreadyAdded.has(r.idRoadmapResource))}
          getOptionLabel={(r) => r.resource}
          isOptionEqualToValue={(a, b) => a.idRoadmapResource === b.idRoadmapResource}
          value={pendingResources}
          onChange={(_, val) => onPendingChange(val)}
          onClose={() => {
            if (pendingResources.length > 0) {
              onAdd(pendingResources);
              onPendingChange([]);
            }
          }}
          label="Add Resource Group"
          size="small"
          fullWidth
        />
      </Box>
      {showAssignDefaults && (
        <Button variant="outlined" size="small" onClick={onAssignDefaults} disabled={!canAssignDefaults}>
          Assign Resources
        </Button>
      )}
    </Box>
  );
});

interface GridSummaryFooterProps {
  totalHours: number;
  plannedHours: number;
  viewOnly: boolean;
}

const GridSummaryFooter = memo(function GridSummaryFooter({
  totalHours,
  plannedHours,
  viewOnly,
}: Readonly<GridSummaryFooterProps>) {
  const totalPercentage = plannedHours > 0 ? Math.round((totalHours / plannedHours) * 100) : 0;
  const hoursColor = totalHours === plannedHours ? 'success.main' : 'error.main';
  const percentageColor = totalPercentage === 100 ? 'success.main' : 'error.main';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        borderTop: 1,
        borderColor: 'divider',
        backgroundColor: 'grey.100',
        fontSize: '0.8125rem',
        fontWeight: 600,
        minHeight: 36,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, px: '10px' }}>Total</Box>
      <Box sx={{ width: 150, px: '10px', color: hoursColor }}>{totalHours}h</Box>
      <Box sx={{ width: 90, px: '10px', color: percentageColor }}>{totalPercentage}%</Box>
      {!viewOnly && <Box sx={{ width: 48 }} />}
    </Box>
  );
});

function FooterWrapper(props: object) {
  const { totalHours, plannedHours, viewOnly } = props as Readonly<GridSummaryFooterProps>;
  return <GridSummaryFooter totalHours={totalHours} plannedHours={plannedHours} viewOnly={viewOnly} />;
}

interface ValidationMessagesProps {
  rowsCount: number;
  plannedHours: number;
  totalHours: number;
}

const ValidationMessages = memo(function ValidationMessages({
  rowsCount,
  plannedHours,
  totalHours,
}: Readonly<ValidationMessagesProps>) {
  const validation = validatePlannedHours(rowsCount, totalHours, plannedHours);
  if (!validation) return null;

  return (
    <Typography variant="caption" sx={{ color: `${validation.level}.main`, mt: 1, display: 'block' }}>
      {validation.message}
    </Typography>
  );
});

export function PlannedHoursSection({
  viewOnly = false,
  createRoadmapMode = false,
  isClone = false,
}: Readonly<PlannedHoursSectionProps>) {
  const {
    rows,
    inputValue,
    plannedHours,
    totalHours,
    pendingResources,
    setPendingResources,
    resources,
    alreadyAdded,
    handleInputChange,
    commitToStore,
    handleAdd,
    handleDelete,
    handleHoursChange,
    showAssignDefaults,
    canAssignDefaults,
    handleAssignDefaults,
  } = usePlannedHoursSection({ createRoadmapMode, isClone });

  const columns = useMemo<GridColDef<ActivityRow>[]>(
    () => [
      {
        field: 'resourceName',
        headerName: 'Resource Groups',
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Box sx={{ color: row.deletedAt ? 'text.disabled' : 'inherit' }}>
            {row.resourceName}
          </Box>
        ),
      },
      {
        field: 'hours',
        headerName: 'Hours',
        width: 150,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <HoursCellInput
            value={row.hours}
            onChange={(value) => handleHoursChange(row.id, value)}
            disabled={viewOnly || plannedHours === 0 || !!row.deletedAt}
          />
        ),
      },
      {
        field: 'percentage',
        headerName: '%',
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => `${plannedHours > 0 ? Math.round((row.hours / plannedHours) * 100) : 0}%`,
      },
      ...(viewOnly
        ? []
        : [
            {
              field: '__delete',
              headerName: '',
              width: 48,
              sortable: false,
              filterable: false,
              renderCell: ({ row }) => (
                <IconButton size="small" onClick={() => handleDelete(row.id)}>
                  <DeleteOutlineOutlined fontSize="small" />
                </IconButton>
              ),
            } satisfies GridColDef<ActivityRow>,
          ]),
    ],
    [plannedHours, handleHoursChange, handleDelete, viewOnly],
  );

  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
          Activities
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            label="Planned Hours"
            type="number"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={commitToStore}
            size="small"
            disabled={viewOnly}
            sx={{ width: 130 }}
          />
          {!viewOnly && (
            <GridControls
              resources={resources}
              alreadyAdded={alreadyAdded}
              pendingResources={pendingResources}
              onPendingChange={setPendingResources}
              onAdd={handleAdd}
              showAssignDefaults={showAssignDefaults}
              canAssignDefaults={canAssignDefaults}
              onAssignDefaults={handleAssignDefaults}
            />
          )}
        </Box>
      </Stack>
      <DataGrid
        rows={rows}
        columns={columns}
        autoHeight
        density="compact"
        disableRowSelectionOnClick
        getRowHeight={() => 'auto'}
        slots={{ footer: FooterWrapper }}
        slotProps={{ footer: { totalHours, plannedHours, viewOnly } as object }}
        sx={{
          border: 0,
          '& .MuiDataGrid-columnHeader': { backgroundColor: 'grey.100' },
          '& .MuiDataGrid-filler': { backgroundColor: 'grey.100' },
        }}
      />
      <ValidationMessages rowsCount={rows.length} plannedHours={plannedHours} totalHours={totalHours} />
    </Box>
  );
}
